
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3, Car } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface InventoryCountItem {
  Category: string;
  Count: number;
}

interface InventoryCountMessageProps {
  data: InventoryCountItem[];
}

const InventoryCountMessage: React.FC<InventoryCountMessageProps> = ({ data }) => {
  const { t, language } = useLanguage();
  // Calculate total inventory
  const totalCount = data.reduce((sum, item) => sum + item.Count, 0);

  return (
    <div className="space-y-3 mb-6">
      {/* In-stock models banner */}
      <div className="flex items-center justify-center h-10 bg-white rounded-md">
        <span className="text-sm font-medium text-gray-700">
          {t('inventory.modelsInInventory')}
        </span>
      </div>
      
      <Card className="my-3 d2cbot-bot_border d2cbot-bot_border-gray-200 shadow-lg bg-gradient-to-br from-white to-gray-50">
       <CardHeader className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-3 d2cbot-bot_border-b d2cbot-bot_border-gray-200 h-auto">
        <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2 text-white">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span>{t('inventory.title')}</span>
          </div>
          <div className="bg-white/20 px-2 py-1 rounded-full text-xs whitespace-nowrap">
            Total: {totalCount}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid gap-3">
          {data.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center justify-between p-3 bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-100 hover:shadow-md transition-all duration-200 hover:d2cbot-bot_border-gray-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                  <Car className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <span className="font-medium text-gray-900 text-sm">
                    {item.Category}
                  </span>
                  <div className="text-xs text-gray-500">
                    {t('inventory.vehicles.available')}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">
                  {item.Count}
                </div>
                <div className="text-xs text-gray-500">
                  {((item.Count / totalCount) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Visual bar chart */}
        <div className="mt-4 pt-4 d2cbot-bot_border-t d2cbot-bot_border-gray-100">
          <div className="space-y-2">
            {data.map((item, index) => {
              const percentage = (item.Count / totalCount) * 100;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="text-xs font-medium text-gray-600 w-16 truncate">
                    {item.Category}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-gray-600 to-gray-800 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 w-12 text-right">
                    {percentage.toFixed(0)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  );
};

export default InventoryCountMessage;
